import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../app/authContext'
import { paths } from '../../app/routes'
import { Tile } from '../../components/ui/Tile'
import { faNumber } from '../../lib/faNumbers'
import { clockFa } from '../../lib/relativeTime'
import { useInstallPrompt } from '../../lib/useInstallPrompt'
import { useHomeStats } from './useHomeStats'
import styles from './HomeScreen.module.css'

export function HomeScreen() {
  const navigate = useNavigate()
  const { operator } = useAuth()
  const { canInstall, install, dismiss } = useInstallPrompt()
  const stats = useHomeStats()

  // The artboard's own strings: «{N} در انتظار · {M} ناموفق» when there is a queue, and
  // «{M} مورد ناموفق» when the queue has drained but failures remain. Its mock hardcodes
  // the failed count, so the zero-failure branch is the one reading it has to settle:
  // «· ۰ ناموفق» would be noise, so the clause drops out.
  const queueSummary = (() => {
    const waiting = `${faNumber(stats.queuedCount)} در انتظار`

    if (stats.failedCount === 0) {
      return waiting
    }

    return stats.queuedCount > 0
      ? `${waiting} · ${faNumber(stats.failedCount)} ناموفق`
      : `${faNumber(stats.failedCount)} مورد ناموفق`
  })()

  return (
    <div className={`${styles.screen} scroll-y`}>
      {canInstall && (
        <div className={styles.installBanner}>
          <div className={styles.installText}>
            این برنامه را به صفحه اصلی دستگاه اضافه کنید تا تمام‌صفحه و آفلاین اجرا شود.
          </div>
          <button
            type="button"
            className={styles.installAction}
            onClick={() => {
              void install()
              dismiss()
            }}
          >
            افزودن
          </button>
        </div>
      )}

      <div className={styles.operatorRow}>
        <div className={styles.operatorName}>اپراتور: {operator?.displayName ?? '—'}</div>
        <div className={styles.operatorMeta}>
          {operator?.shift ? `${operator.shift} · انبار مرکزی` : 'انبار مرکزی'}
        </div>
      </div>

      <section className={styles.welcomeCard} aria-label="خلاصه شیفت">
        <div className={styles.welcomeGlow} aria-hidden="true" />
        <div className={styles.welcomeCopy}>
          <span className={styles.eyebrow}>شیفت آماده است</span>
          <strong>از اینجا شروع کنید</strong>
          <span>اسکن سریع، ثبت آفلاین، هماهنگ با شبکه</span>
        </div>
        <div className={styles.liveBadge}><span className={styles.liveDot} /> آماده</div>
      </section>

      <div className={styles.sectionLabel}>شروع کار — یکی را انتخاب کنید</div>

      <div className={styles.tiles}>
        <Tile
          glyph="▣"
          label="اسکن پالت"
          hint="جریان پالت‌محور"
          accent="var(--primary)"
          iconInk="var(--primary-ink)"
          chevron="var(--primary)"
          onClick={() => navigate(paths.pallets)}
        />

        <Tile
          glyph="⛃"
          label="اسکن آزاد"
          hint="سبد موقت · سریال‌محور"
          accent="var(--success)"
          iconInk="#06170E"
          chevron="var(--success-bright)"
          onClick={() => navigate(paths.basket)}
        />

        {/*
          The dispatch-order flow is deferred to v2, so the tile renders disabled rather
          than being removed — the operator sees the capability exists and is not yet on.
        */}
        <Tile
          glyph="⇄"
          label="دستور ارسال"
          hint="افزودن پالت · صدور حواله"
          accent="var(--text-muted)"
          iconInk="#11161C"
          chevron="#C3CBD5"
          disabled
          deferredNote="نسخه ۲"
        />

        <Tile
          glyph="⇅"
          label="صف ارسال"
          hint={queueSummary}
          accent="var(--danger)"
          iconInk="var(--danger-bg-deep)"
          chevron="#FF9195"
          onClick={() => navigate(paths.queue)}
        />
      </div>

      <div className={`${styles.sectionLabel} ${styles.sectionLabelSpaced}`}>وضعیت امروز</div>

      <div className={styles.stats}>
        <div className={styles.statsHeader}><span>خلاصه عملکرد</span><span className={styles.statsPulse}>• امروز</span></div>
        <div className={styles.statRow}>
          <span className={styles.statKey}>اسکن‌های امروز</span>
          <span className={styles.statValue}>{faNumber(stats.todayCount)}</span>
        </div>
        <div className={styles.statRow}>
          <span className={styles.statKey}>آخرین ارسال موفق</span>
          <span className={styles.statValue}>
            {stats.lastSyncAt === null ? '—' : clockFa(stats.lastSyncAt)}
          </span>
        </div>
        <div className={styles.statRow}>
          <span className={styles.statKey}>پالت باز فعلی</span>
          <span className={stats.openPalletCode === null ? styles.statValue : styles.statCode}>
            {stats.openPalletCode ?? '—'}
          </span>
        </div>
      </div>
    </div>
  )
}
